import type React from "react"
import { useCallback, useEffect, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import moment from "moment"
import { useLocation, useNavigate } from "react-router-dom"
import { Icon, Menu, Popup, Tab } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { RichTextViewer } from "simplydo/react/richtext"
import styled from "styled-components"

import api, { asQuery, websocketApi } from "api"
import { useAppSelector } from "store"
import useTheme from "theme/useTheme"
import util from "utils/utils"

import CircularButtonWithNumber from "../CircularButtonWithNumber"
import ModalCarousel from "../ModalCarousel"
import Downloads from "./Downloads"
import Notifications from "./Notifications"

const StyledNotificationTray = styled.div`
  min-width: ${({ theme }) => (theme.sizes.isMobile ? "100%" : "500px;")};
  max-height: ${({ theme }) => (theme.sizes.isMobile ? null : "600px")} !important;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  z-index: 9999;
`

type NotificationsType = OpenAPI.GET<`/notifications`>["response"]["notifications"]

type NotificationTrayProps = {
  isMobile?: boolean
  style?: React.CSSProperties
}

export const useTrayStatus = () => {
  type TrayStatus = {
    open: boolean
    activeIndex: number
  }

  const defaultState: TrayStatus = {
    open: false,
    activeIndex: 0,
  }
  const openState = useQuery({
    queryKey: ["notificationTray"],
    queryFn: async () => defaultState,
  })

  const setTrayStatus = useCallback((trayStatus: Partial<TrayStatus>) => {
    api.queryClient.setQueryData(["notificationTray"], (prevStatus: TrayStatus) => ({
      ...prevStatus,
      ...trayStatus,
    }))
  }, [])

  return [openState.data || defaultState, setTrayStatus] as const
}

const NotificationTray = ({ isMobile, style }: NotificationTrayProps) => {
  const theme = useTheme()
  const user = useAppSelector((state) => state.user)
  const navigate = useNavigate()
  const location = useLocation()

  const [trayStatus, setTrayStatus] = useTrayStatus()
  const [invitations, setInvitations] = useState([])
  const [page, setPage] = useState(1)
  const [downloadCompleted, setDownloadCompleted] = useState(false)
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false)
  const [announcementNotification, setAnnouncementNotification] = useState<NotificationsType[number]>(null)

  const userId = user?._id
  // Non-acceptable invitations aren't really interesting to the user in terms of notification content. They may be able to remove them, but it's not really "notification" worthy that e.g., another admin has invited a user to one of their groups
  const acceptableInvitations = useMemo(
    () => invitations.filter((invitation) => !invitation.createdByCurrentUser && invitation.canAccept),
    [invitations],
  )

  const getInvitations = useCallback(() => {
    api.invitations.get(
      ({ invitations: newInvitations }) => setInvitations(newInvitations),
      () => {},
    )
  }, [])

  useEffect(() => {
    if (!userId) return
    const notificationSubscription = websocketApi.subscribe(`pushNotification-${userId}`, () => {
      api.queryClient.invalidateQueries({
        queryKey: ["get", "users", "notifications"],
      })
    })
    return notificationSubscription.unsubscribe
  }, [userId])

  useEffect(() => {
    if (!userId) return
    const downloadSubscription = websocketApi.subscribe(`downloadCompleted-${userId}`, () => {
      setDownloadCompleted(true)
      setTrayStatus({ open: true, activeIndex: 1 })
      api.queryClient.invalidateQueries({
        queryKey: ["get", "users", "downloads"],
      })
    })
    return downloadSubscription.unsubscribe
  }, [userId, setTrayStatus])

  useEffect(() => {
    getInvitations()
  }, [getInvitations])

  const notificationsQuery = useQuery({
    queryKey: ["get", "users", "notifications", page, userId],
    queryFn: asQuery(api.notifications.get, {
      params: [page],
    })!,
    enabled: !!userId,
    refetchInterval: ({ state }) => {
      if (state.data?.notifications.filter((x) => x.status === "pending").length > 0) {
        return 10000
      }
      return false
    },
  })
  const { total, unseen, notifications } = notificationsQuery.data || { notifications: [], total: 0, unseen: 0 }

  const downloadsQuery = useQuery({
    queryKey: ["get", "users", "downloads"],
    queryFn: asQuery(api.users.getDownloads, {
      params: [userId, { limit: 10, page: page }],
    })!,
    enabled: !!userId,
    refetchInterval: ({ state }) => {
      if (state.data?.pendingDownloads?.length > 0) {
        return 10000
      }
      return false
    },
  })

  const markAsSeen = useCallback((newNotifications, markAllAsSeen) => {
    api.notifications.markAsSeen(
      newNotifications,
      markAllAsSeen,
      () => {
        api.queryClient.invalidateQueries({ queryKey: ["get", "users", "notifications"] })
      },
      () => {},
    )
  }, [])

  const markAllAsSeen = useCallback(() => {
    markAsSeen([], true)
  }, [markAsSeen])
  const notificationContent = (
    <StyledNotificationTray>
      <Tab
        defaultActiveIndex={0}
        activeIndex={trayStatus.activeIndex}
        onTabChange={(_e, { activeIndex }) => {
          setTrayStatus({ activeIndex: activeIndex as number })
        }}
        menu={{ secondary: true, pointing: true }}
        style={{ position: "relative", margin: 0 }}
        panes={[
          {
            menuItem: (
              <Menu.Item>
                <Icon name="bell" />
                Notifications
              </Menu.Item>
            ),
            render: () => (
              <Notifications
                notifications={notifications}
                total={total}
                acceptableInvitations={acceptableInvitations}
                invitations={invitations}
                setInvitations={setInvitations}
                page={page}
                setPage={setPage}
                setAnnouncementNotification={setAnnouncementNotification}
                setAnnouncementModalOpen={setAnnouncementModalOpen}
                loading={notificationsQuery.isLoading}
              />
            ),
          },
          {
            menuItem: (
              <Menu.Item>
                <Icon name="download" />
                Downloads
              </Menu.Item>
            ),
            render: () => <Downloads downloadCompleted={downloadCompleted} downloadsQuery={downloadsQuery} />,
          },
        ]}
      />
    </StyledNotificationTray>
  )

  if (isMobile) {
    return notificationContent
  }
  return (
    <>
      {announcementNotification && (
        <ModalCarousel
          title={`Received ${util.pluralise(announcementNotification?.events?.length, "announcement", "announcements")}`}
          open={announcementModalOpen}
          onClose={() => setAnnouncementModalOpen(false)}
          screens={announcementNotification?.events?.map((event) => ({
            title: event.ownerAnnouncement?.subject,
            content: (
              <div>
                {event.ownerAnnouncement.body ? (
                  <RichTextViewer content={event.ownerAnnouncement?.body} />
                ) : (
                  <p>{event.ownerAnnouncement?.text}</p>
                )}
                {event.ownerAnnouncement?.attachments?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <h5>Attachments</h5>
                    {event.ownerAnnouncement.attachments.map((attachment) => (
                      <a
                        href={attachment.url}
                        key={attachment.name}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={attachment.name}
                        className="ui small blue button"
                        style={{ borderRadius: 5 }}
                      >
                        <i className="paperclip icon"></i>
                        {attachment.name}
                      </a>
                    ))}
                  </div>
                )}
                <p style={{ color: "grey" }}>
                  {event?.ownerAnnouncement?.createdAt
                    ? ` ${moment(event.ownerAnnouncement.createdAt).format("DD/MM/YY HH:mm")}`
                    : ""}
                </p>
              </div>
            ),
          }))}
          actions={[
            {
              label: "Close",
              icon: "check",
              primary: true,
              onClick: () => {
                setAnnouncementModalOpen(false)
              },
            },
          ]}
        />
      )}

      <Popup
        on="click"
        style={{ width: "fit-content" }}
        positionFixed
        wide="very"
        position="bottom right"
        offset={[5, 0]}
        open={trayStatus.open}
        onClose={() => {
          setDownloadCompleted(false)
          setTrayStatus({ open: false })
        }}
        trigger={
          <div style={style}>
            <CircularButtonWithNumber
              onClick={() => {
                if (!trayStatus.open) {
                  markAllAsSeen()
                }
                if (theme.sizes.isMobile) {
                  if (location.pathname === "/notifications") {
                    navigate("..")
                  } else {
                    navigate("/notifications")
                  }
                } else {
                  if (!trayStatus.open) {
                    api.queryClient.invalidateQueries({
                      queryKey: ["get", "users", "downloads"],
                    })
                  }
                  setDownloadCompleted(false)
                  setTrayStatus({ open: !trayStatus.open })
                }
              }}
              icon="bell"
              count={unseen === 0 && acceptableInvitations.length !== 0 ? "●" : unseen}
            />
          </div>
        }
        content={notificationContent}
      />
    </>
  )
}

export default NotificationTray
